import * as React from "react";
import { pageChangeToReport } from "./paginationReport";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    OnChangeFn,
    PaginationState,
    SortingState,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  emptyMessage?: React.ReactNode;
  pageSize?: number;
  /** 1-basierte Seite; macht die Paginierung von außen steuerbar (z. B. URL-Parameter). */
  page?: number;
  /** Begleiter zu `page`; erhält die neue 1-basierte Seite. */
  onPageChange?: (page: number) => void;
  /**
   * Gesamtzahl der Zeilen, wenn `data` nur eine Seite davon ist.
   *
   * Gesetzt heißt: der Server pagiert. Die Tabelle darf dann nicht selbst
   * blättern — sie kennt die anderen Seiten nicht — und die Seitenzahl kommt
   * aus dieser Zahl statt aus der Länge von `data`.
   */
  totalRows?: number;
  /** Klick auf eine Zeile (Zeilen werden dann als klickbar dargestellt). */
  onRowClick?: (row: TData) => void;
  /** Ladezustand: zeigt Skeleton-Zeilen statt Daten. */
  loading?: boolean;
  loadingRowCount?: number;
  rowClassName?: (row: TData) => string | undefined;
  previousAriaLabel?: string;
  nextAriaLabel?: string;
  /**
   * Die Zeilen, die gerade zu sehen sind (Seite und Sortierung berücksichtigt) — für
   * Aufrufer, die Zusatzdaten nur für die sichtbare Seite nachladen.
   */
  onVisibleRowsChange?: (rows: TData[]) => void;
  /** Sortierung beim ersten Rendern, z. B. nach Schweregrad; danach sortiert der Benutzer. */
  initialSorting?: SortingState;
  /**
   * Sichtbarkeit je Spalten-`id` (`false` = ausgeblendet), von außen gesteuert — für eine
   * „Spalten“-Auswahl über der Tabelle. Ohne Angabe sind alle Spalten sichtbar.
   */
  columnVisibility?: VisibilityState;
  /**
   * Sortierung von außen — für Tabellen, die der Server sortiert (zusammen mit `totalRows`).
   * Gesetzt heißt: die Tabelle sortiert die gelieferte Seite nicht selbst, sie meldet nur den
   * gewünschten Stand.
   */
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

/** Skeleton widths in percent; cycled through rows and columns. */
const SKELETON_WIDTHS = [72, 48, 86, 60, 40, 78];

export function DataTable<TData, TValue>({
  columns,
  data,
  emptyMessage,
  pageSize = 10,
  page,
  onPageChange,
  totalRows,
  onRowClick,
  loading,
  loadingRowCount = 5,
  rowClassName,
  previousAriaLabel = "Previous page",
  nextAriaLabel = "Next page",
  onVisibleRowsChange,
  initialSorting,
  columnVisibility,
  sorting: externalSorting,
  onSortingChange: onExternalSortingChange,
}: DataTableProps<TData, TValue>) {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>(initialSorting ?? []);
  const sortingControlled = externalSorting !== undefined && onExternalSortingChange !== undefined;
  const sorting = sortingControlled ? externalSorting : internalSorting;
  const setSorting: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    if (sortingControlled) onExternalSortingChange(next);
    else setInternalSorting(next);
  };
  const [internalPagination, setInternalPagination] =
    React.useState<PaginationState>({ pageIndex: 0, pageSize });

  const controlled = page !== undefined;
  const requestedPageIndex = controlled
    ? Math.max(0, page - 1)
    : internalPagination.pageIndex;

  const serverPaged = totalRows !== undefined;
  const rowCount = serverPaged ? (totalRows ?? 0) : data.length;
  const pageCount = Math.ceil(rowCount / pageSize);

  /*
   * Klammerung auf die letzte Seite.
   *
   * TanStack springt sonst bei jeder Datenänderung auf die erste Seite zurück (`autoResetPageIndex`,
   * unten abgeschaltet). Ohne diese Rücksetzung braucht es aber eine Grenze nach oben: Schrumpfen
   * die Daten unter die gewählte Seite — oder steht in der Adresse eine Seite, die es nicht mehr
   * gibt —, zeigt die Tabelle die letzte, die es gibt. Nicht während des Ladens und nicht bei
   * leeren Daten: Ein leerer Zwischenstand darf die gewählte Seite nicht verwerfen.
   */
  const clampedPageIndex =
    loading || pageCount === 0
      ? requestedPageIndex
      : Math.min(requestedPageIndex, pageCount - 1);

  const paginationState: PaginationState = { pageIndex: clampedPageIndex, pageSize };

  const onPageChangeRef = React.useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  React.useEffect(() => {
    if (clampedPageIndex === requestedPageIndex) {
      return;
    }

    if (controlled) {
      onPageChangeRef.current?.(clampedPageIndex + 1);
    } else {
      setInternalPagination((previous) => ({ ...previous, pageIndex: clampedPageIndex }));
    }
  }, [clampedPageIndex, requestedPageIndex, controlled]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next =
      typeof updater === "function" ? updater(paginationState) : updater;
    if (controlled) {
      /*
       * Nur melden, was die Seite wirklich ändert.
       *
       * TanStack setzt die Seite von sich aus zurück, sobald sich die Daten ändern — auch dann,
       * wenn sie schon auf der ersten steht. Wer die Seite in die Adresse legt (`useUrlPageState`),
       * schließt damit einen Kreis: Adresse ändert sich, Tabelle rendert, Rücksetzung meldet,
       * Adresse ändert sich. Chrome bremst das schließlich selbst aus — „Throttling navigation to
       * prevent the browser from hanging" — und bis dahin ist die Seite nicht zu bedienen. Das war
       * kein gedachter Fall: Er stand so im Fehlerprotokoll, mit `data-table` und
       * `dataTablePagination` untereinander im Aufrufstapel.
       */
      const report = pageChangeToReport(paginationState.pageIndex, next.pageIndex);

      if (report !== null) {
        onPageChange?.(report);
      }
    } else {
      setInternalPagination(next);
    }
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination: paginationState,
      ...(columnVisibility ? { columnVisibility } : {}),
    },
    onSortingChange: setSorting,
    onPaginationChange: handlePaginationChange,
    /*
     * TanStack setzt die Seite bei jeder Datenänderung auf die erste zurück (Standard). Mit einer
     * Seite aus der Adresse (`useUrlPageState`) war damit Seite 2 unerreichbar: Klick auf „weiter“,
     * Adresse auf 2, Daten kommen neu, Rücksetzung meldet Seite 1, Adresse zurück auf 1 (#1186).
     * Die Seite bleibt stehen; was über das Ende hinausragt, klammert der Effekt oben.
     */
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    ...(sortingControlled ? { manualSorting: true as const } : { getSortedRowModel: getSortedRowModel() }),
    // Ohne `manualPagination` würde die Tabelle die eine gelieferte Seite noch
    // einmal zerschneiden und ab Seite 2 leer bleiben.
    ...(serverPaged
      ? { manualPagination: true as const, rowCount: totalRows }
      : { getPaginationRowModel: getPaginationRowModel() }),
  });

  const { pageIndex } = table.getState().pagination;
  const showPagination = !loading && rowCount > pageSize;

  const onVisibleRowsChangeRef = React.useRef(onVisibleRowsChange);
  onVisibleRowsChangeRef.current = onVisibleRowsChange;

  // Nur melden, wenn sich Daten, Seite oder Sortierung ändern — nicht bei jedem Rendern.
  React.useEffect(() => {
    if (!onVisibleRowsChangeRef.current) {
      return;
    }
    onVisibleRowsChangeRef.current(
      loading ? [] : table.getRowModel().rows.map((row) => row.original),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sorting, pageIndex, pageSize, loading]);

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={(header.column.columnDef.meta as any)?.className}
                  style={{
                    width: header.getSize() === 150 ? undefined : header.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
              <TableRow
                key={`skeleton-${rowIndex}`}
                aria-hidden
                // Rows fade in one after the other, so the table builds up instead of popping in.
                className="animate-in fade-in-0 fill-mode-both duration-300"
                style={{ animationDelay: `${rowIndex * 50}ms` }}
              >
                {table.getVisibleLeafColumns().map((column, cellIndex) => (
                  <TableCell key={cellIndex}>
                    {(column.columnDef.meta as any)?.skeleton === "icon" ? (
                      // A single row-menu button.
                      <div className="flex justify-end">
                        <Skeleton className="size-8 rounded-md" />
                      </div>
                    ) : column.id === "actions" ? (
                      // The icon buttons of an action column.
                      <div className="flex justify-center gap-2">
                        <Skeleton className="size-6 rounded-md" />
                        <Skeleton className="size-6 rounded-md" />
                        <Skeleton className="size-6 rounded-md" />
                      </div>
                    ) : (
                      // Varying widths per cell, so the placeholder reads as text and not as a bar.
                      <Skeleton
                        className="h-4"
                        style={{
                          width: `${SKELETON_WIDTHS[(rowIndex + cellIndex * 2) % SKELETON_WIDTHS.length]}%`,
                        }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() ? "selected" : undefined}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={cn(
                  onRowClick && "cursor-pointer",
                  rowClassName?.(row.original),
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={(cell.column.columnDef.meta as any)?.className}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={table.getVisibleLeafColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                {emptyMessage ?? "No data."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <div className="flex items-center justify-end gap-2 px-4 py-2 border-t">
          <div className="text-sm text-muted-foreground mr-auto">
            {pageIndex + 1} / {Math.max(pageCount, 1)}
          </div>
          <Button
            variant="outline"
            size="sm"
            aria-label={previousAriaLabel}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label={nextAriaLabel}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
